import { motion } from 'framer-motion';
import { 
  CheckSquare, AlertTriangle, Video, Clock, ArrowUpRight, ArrowDownRight,
  Plus, Upload, FileText, TrendingUp, Loader2
} from 'lucide-react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { getPermissions } from '../../utils/permissions';

const allQuickActions = [
  { icon: Upload, label: 'Upload Meeting', color: 'from-blue-500 to-cyan-500', navigate: 'meetings', permission: 'canCreateMeeting' },
  { icon: Plus, label: 'Create Task', color: 'from-purple-500 to-pink-500', navigate: 'tasks', permission: 'canCreateTask' },
  { icon: AlertTriangle, label: 'Log Risk', color: 'from-orange-500 to-red-500', navigate: 'risk', permission: 'canCreateRisk' },
  { icon: FileText, label: 'New Policy', color: 'from-green-500 to-emerald-500', navigate: 'settings', permission: 'canViewSettings' },
];

export const DashboardHome = ({ onNavigate }) => {
  const { getStats, getRecentActivity, meetings } = useData();
  const { user } = useAuth();
  const perms = getPermissions(user?.role);
  const stats = getStats();
  const recentActivity = getRecentActivity();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const quickActions = allQuickActions.filter(a => perms[a.permission]);

  const kpiCards = [
    { 
      title: 'Total Tasks', 
      value: String(stats.totalTasks), 
      change: `${stats.completionRate}% done`,
      trend: 'up',
      icon: CheckSquare,
      color: 'from-blue-500 to-blue-600'
    },
    { 
      title: 'Overdue Tasks', 
      value: String(stats.overdueTasks), 
      change: stats.overdueTasks > 0 ? 'Needs attention' : 'All clear',
      trend: stats.overdueTasks > 0 ? 'up' : 'down',
      icon: Clock,
      color: 'from-orange-500 to-orange-600'
    },
    { 
      title: 'Active Risks', 
      value: String(stats.openRisks), 
      change: `${stats.highRisks} high`,
      trend: stats.highRisks > 0 ? 'up' : 'down',
      icon: AlertTriangle,
      color: 'from-red-500 to-red-600'
    },
    { 
      title: 'Meetings Processed', 
      value: String(stats.processedMeetings), 
      change: `of ${meetings.length} total`,
      trend: 'up',
      icon: Video,
      color: 'from-green-500 to-green-600'
    },
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'meeting': return <Video className="w-5 h-5" />;
      case 'task': return <CheckSquare className="w-5 h-5" />;
      case 'risk': return <AlertTriangle className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'meeting': return 'bg-blue-500/20 text-blue-400';
      case 'task': return 'bg-green-500/20 text-green-400';
      case 'risk': return 'bg-red-500/20 text-red-400';
      default: return 'bg-purple-500/20 text-purple-400';
    }
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'AI Processed': return 'bg-blue-500/20 text-blue-400';
      case 'Completed': return 'bg-green-500/20 text-green-400';
      case 'Auto-Detected': return 'bg-orange-500/20 text-orange-400';
      case 'Manual': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-purple-500/20 text-purple-400';
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div className="space-y-6" data-testid="dashboard-home">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Welcome back, {firstName}</h1>
          <p className="text-gray-400 mt-1">Here's what's happening with your operations today.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-400 text-sm font-medium border border-blue-500/20">
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400 mr-2 animate-pulse"></span>
            AI Active
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 tour-kpi-grid">
        {kpiCards.map((card, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`relative p-5 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-200 group overflow-hidden tour-kpi-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
            data-testid={`kpi-card-${card.title.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {/* Gradient glow */}
            <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${card.color} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`}></div>
            
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl bg-gradient-to-br ${card.color}`}>
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <span className={`flex items-center gap-1 text-sm font-medium ${
                  card.trend === 'up' ? 'text-green-400' : 'text-red-400'
                }`}>
                  {card.trend === 'up' ? (
                    <ArrowUpRight className="w-4 h-4" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4" />
                  )}
                  {card.change}
                </span>
              </div>
              <p className="text-3xl font-bold text-white mb-1">{card.value}</p>
              <p className="text-sm text-gray-400">{card.title}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 tour-quick-actions">
        {quickActions.map((action, index) => (
          <motion.button
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
            onClick={() => onNavigate && onNavigate(action.navigate)}
            className={`p-4 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-200 group tour-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
            data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
              <action.icon className="w-6 h-6 text-white" />
            </div>
            <p className="text-sm font-medium text-white">{action.label}</p>
          </motion.button>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Feed */}
        <div className="lg:col-span-2 p-5 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-semibold text-white">Recent Activity</h2>
            <button className="text-sm text-blue-400 hover:text-blue-300 transition-colors">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentActivity.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                <p className="text-sm">Processing activity data...</p>
              </div>
            ) : (
              recentActivity.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors cursor-pointer group"
                  data-testid={`activity-item-${activity.id}`}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-white truncate">{activity.title}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getBadgeColor(activity.badge)}`}>
                        {activity.badge}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400">{activity.description}</p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{formatTime(activity.time)}</span>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* AI Insights Panel */}
        <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-xl border border-blue-500/20 tour-ai-insights">
          <div className="flex items-center gap-2 mb-5">
            <TrendingUp className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">AI Insights</h2>
            <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 text-xs font-medium">
              Live
            </span>
          </div>
          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-gray-300 mb-2">
                <span className="text-blue-400 font-medium">Productivity Alert:</span> Task completion rate is at {stats.completionRate}%.
              </p>
              <p className="text-xs text-gray-500">Based on {stats.totalTasks} tracked tasks</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-gray-300 mb-2">
                <span className="text-orange-400 font-medium">Risk Pattern:</span> {stats.highRisks} high-severity risk{stats.highRisks !== 1 ? 's' : ''} detected across projects.
              </p>
              <p className="text-xs text-gray-500">Recommend unified mitigation strategy</p>
            </div>
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <p className="text-sm text-gray-300 mb-2">
                <span className="text-green-400 font-medium">Meetings:</span> {stats.processedMeetings} of {meetings.length} meetings AI-processed.
              </p>
              <p className="text-xs text-gray-500">{meetings.length - stats.processedMeetings} pending analysis</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
